import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

import type { SupportedFormHelpTextServerConfigKeys } from 'shared-types';

interface Props {
  children: React.ReactNode;
  darkMode?: boolean;
  error?: string;
  errorAfterFormElement?: boolean;
  highlight?: 'border-left' | 'border';
  htmlFor?: string;
  label?: string | React.ReactNode;
  labelAction?: React.ReactNode;
  labelHelpText?: string | React.ReactNode;
  labelHelpTextServerConfigKey?: SupportedFormHelpTextServerConfigKeys;
  labelIsOptional?: boolean;
  labelTooltip?: string;
  maxWidth?: string;
  size?: 'small' | 'medium' | 'large' | 'huge';
  style?: React.CSSProperties;
}

const FormGroup = ({
  children,
  darkMode,
  error,
  errorAfterFormElement,
  highlight,
  htmlFor,
  label,
  labelAction,
  labelHelpText,
  labelHelpTextServerConfigKey,
  labelIsOptional,
  labelTooltip,
  maxWidth,
  size = 'medium',
  style
}: Props) => {
  const {
    language,
    messages,
    theme,
    serverConfig: { formHelpTextOptions }
  } = useReduxState();

  const globalHelpText =
    formHelpTextOptions?.[labelHelpTextServerConfigKey]?.translations?.[
      language
    ] || formHelpTextOptions?.[labelHelpTextServerConfigKey]?.value;

  return (
    <div className={highlight + ' form-group'} style={style}>
      {label || labelAction ? (
        <div className="label-row">
          {label ? (
            <label
              className="label"
              htmlFor={htmlFor}
              style={{
                fontSize: {
                  small: '1.4rem',
                  medium: '1.5rem',
                  large: '1.6rem',
                  huge: '2rem'
                }[size]
              }}
            >
              {labelTooltip ? (
                <div aria-label={labelTooltip} data-tooltip={labelTooltip}>
                  {label}
                  {labelIsOptional ? (
                    <span className="optional">{` (${messages.commonOptional})`}</span>
                  ) : null}
                  <Icon
                    icon="question-circle"
                    style={{
                      fontSize: {
                        small: '1.1rem',
                        medium: '1.2rem',
                        large: '1.3rem',
                        huge: '1.5rem'
                      }[size],
                      marginLeft: 5,
                      position: 'relative',
                      top: -1
                    }}
                  />
                </div>
              ) : (
                <div>
                  {label}
                  {labelIsOptional ? (
                    <span className="optional">{` (${messages.commonOptional})`}</span>
                  ) : null}
                </div>
              )}
              {globalHelpText || labelHelpText ? (
                <div
                  className="help-text"
                  style={{
                    fontSize: {
                      small: '1.2rem',
                      medium: '1.3rem',
                      large: '1.4rem',
                      huge: '1.6rem'
                    }[size]
                  }}
                >
                  {globalHelpText || labelHelpText}
                </div>
              ) : null}
            </label>
          ) : null}

          {labelAction ? labelAction : null}
        </div>
      ) : null}

      {error && !errorAfterFormElement ? (
        <div className="error-message error-message-before">{error}</div>
      ) : null}

      <div className="form-element">{children}</div>

      {error && errorAfterFormElement ? (
        <div className="error-message error-message-after">{error}</div>
      ) : null}

      <style jsx>{`
        .form-group:not(:first-child) {
          margin-top: 15px;
        }
        .label {
          color: ${darkMode ? '#fff' : theme.textColorLight};
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 600;
          padding: 0 0 5px;
        }
        .label-row {
          align-items: baseline;
          display: flex;
          gap: 10px;
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          font-weight: 400;
        }
        .error-message-before {
          margin: -5px 0 5px;
        }
        .error-message-after {
          margin-top: 5px;
        }
        .form-element {
          max-width: ${maxWidth || '540px'};
        }
        .help-text {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: normal;
          max-width: ${maxWidth || '540px'};
        }
        .optional {
          color: ${theme.textColorLighter};
          font-weight: 400;
        }
        .border-left {
          border-left: 2px solid ${theme.lightGrey};
          padding: 0 10px;
          margin: 10px 0;
        }
        .border {
          border: 2px solid ${theme.lightGrey};
          border-radius: 4px;
          padding: 10px;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
};

export default FormGroup;
